import { motion } from "framer-motion";
import { ArrowUpRight, Globe } from "lucide-react";
import { DEMOS } from "@/data";
import { useLang } from "@/i18n";

function DemoCard({ demo, index, t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.08 }}
      data-testid={`demo-${demo.id}`}
      className="group relative border border-[#27272A] bg-[#0c0c0e] transition-all duration-200 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-brutal"
    >
      <div className="flex items-center gap-2 px-4 h-11 border-b border-[#27272A] bg-[#111113]">
        <span className="w-3 h-3 rounded-full bg-brand" />
        <span className="w-3 h-3 rounded-full bg-[#3f3f46]" />
        <span className="w-3 h-3 rounded-full bg-[#3f3f46]" />
        <span className="ml-3 font-satoshi text-xs text-[#71717a] truncate">
          {demo.url.replace(/^https?:\/\//, "").split("/")[0]}
        </span>
      </div>

      <div className="relative aspect-[16/9] overflow-hidden border-b border-[#27272A]">
        <img
          src={demo.image}
          alt={demo.name}
          loading="lazy"
          className="w-full h-full object-cover object-top scale-100 group-hover:scale-[1.04] transition-transform duration-[800ms] ease-out"
        />
        <a
          href={demo.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`demo-visit-${demo.id}`}
          className="absolute inset-0 flex items-center justify-center bg-[#09090B]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        >
          <span className="inline-flex items-center gap-2 font-satoshi font-bold text-sm uppercase tracking-widest px-6 py-3 bg-brand text-white">
            {t.portfolio.visit} <ArrowUpRight size={16} />
          </span>
        </a>
      </div>

      <div className="flex items-end justify-between p-6">
        <div>
          <p className="font-satoshi font-bold text-xs uppercase tracking-widest text-accent mb-2 flex items-center gap-2">
            <Globe size={12} /> {t.portfolio.tag}
          </p>
          <h3 className="font-cabinet font-black uppercase text-2xl md:text-3xl tracking-tight leading-none">
            {demo.name}
          </h3>
          <p className="font-satoshi text-sm text-[#a1a1aa] mt-3 max-w-sm">{t.portfolio.demos[demo.id]}</p>
        </div>
        <a
          href={demo.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`demo-arrow-${demo.id}`}
          className="w-11 h-11 shrink-0 border border-[#27272A] flex items-center justify-center hover:bg-brand hover:text-white transition-colors duration-200"
        >
          <ArrowUpRight size={18} />
        </a>
      </div>
    </motion.div>
  );
}

export default function Portfolio() {
  const { t } = useLang();
  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32 border-b border-[#27272A]"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
        <div>
          <p className="font-satoshi font-bold text-xs tracking-[0.3em] uppercase text-accent mb-5 flex items-center gap-2">
            <span className="w-4 h-4 bg-brand" /> {t.portfolio.label}
          </p>
          <h2 className="font-cabinet font-black uppercase text-6xl md:text-8xl tracking-tighter leading-[0.8]">
            {t.portfolio.h1}
            <br />
            <span className="text-outline">{t.portfolio.h2}</span>
          </h2>
        </div>
        <p className="font-satoshi text-lg text-[#a1a1aa] max-w-sm md:text-right">
          {t.portfolio.intro}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {DEMOS.map((d, i) => (
          <DemoCard key={d.id} demo={d} index={i} t={t} />
        ))}
      </div>
    </section>
  );
}
